import { useEffect, useRef, useState, type ReactNode } from 'react';
import { usePrefersReducedMotion } from '@/shared/hooks/usePrefersReducedMotion';

/**
 * TypewriterWord — the hero's Lime accent word that types/deletes through a
 * list with a blinking caret (extraction §18).
 *
 * Natural width: the live word hugs its own text (no fixed reserved slot), so
 * the words that follow it sit immediately after the caret instead of after a
 * gap sized to the longest word. As the word types/deletes, the trailing text
 * glides horizontally on the same centered line — intentional motion, not a
 * layout jump. Reduced motion shows the first word statically (the caret
 * keyframe is itself gated by `prefers-reduced-motion` in globals.css).
 */
export function TypewriterWord({ words }: { words: string[] }): ReactNode {
  const reduced = usePrefersReducedMotion();
  const [text, setText] = useState(words[0] ?? '');

  // Start fully typed but in the deleting phase: the first word shows, then
  // erases letter-by-letter, then the next word types in, and so on.
  const state = useRef({ word: 0, char: words[0]?.length ?? 0, deleting: true });
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (reduced || words.length < 2) return undefined;

    const tick = (): void => {
      const s = state.current;
      const current = words[s.word] ?? '';
      let delay = s.deleting ? 55 : 130;

      if (s.deleting) {
        s.char -= 1;
        setText(current.slice(0, s.char));
        if (s.char === 0) {
          s.deleting = false;
          s.word = (s.word + 1) % words.length;
          delay = 480;
        }
      } else {
        s.char += 1;
        setText(current.slice(0, s.char));
        if (s.char === current.length) {
          s.deleting = true;
          delay = 1900;
        }
      }
      timer.current = window.setTimeout(tick, delay);
    };

    timer.current = window.setTimeout(tick, 1400);
    return () => window.clearTimeout(timer.current);
  }, [reduced, words]);

  return (
    <span
      className={`text-secondary-fixed ${reduced ? '' : 'typewriter-cursor'}`}
    >
      {reduced ? words[0] : text}
    </span>
  );
}

export default TypewriterWord;
